export const TICKET_STORAGE_KEY = 'ticket_detail';
/** Per-tab backup so a shared-origin /scan clearTicket cannot wipe main-display. */
export const TICKET_SESSION_KEY = 'ticket_detail_session';

export const DEFAULT_EXPIRED_TICKET_MESSAGE =
  'This ticket has expired. Please purchase a new ticket.';

export const DEFAULT_INVALID_MESSAGE = 'Invalid ticket. Check ticket date & time.';

export const getTicketIdFromScan = (decodedText) => {
  const value = String(decodedText || '').trim();
  if (!value) return '';

  try {
    const scannedUrl = new URL(value);
    return scannedUrl.searchParams.get('ticketId') || value;
  } catch {
    return value;
  }
};

export const getExtendTicketUrl = (ticketId) => {
  const webUrl = process.env.REACT_APP_FRONTEND_BASE_URL?.trim();
  if (!webUrl || !ticketId) return '';

  try {
    const extendUrl = new URL('/extend-ticket', webUrl);
    extendUrl.searchParams.set('ticketId', ticketId);
    return extendUrl.toString();
  } catch {
    return '';
  }
};

export const pickTicket = (data = {}) => {
  if (data.ticket && typeof data.ticket === 'object' && data.ticket.id) {
    return data.ticket;
  }
  if (data.id) return data;
  return null;
};

export const isTicketExpired = (data = {}) =>
  data.isExpired === true ||
  data.ticket?.ticket_status?.toLowerCase() === 'expired' ||
  data.ticketStatus?.toLowerCase() === 'expired';

export const isTicketExtendable = (data = {}) =>
  data.isExtendable === true || data.canExtend === true;

const parseTicket = (raw) => {
  if (!raw) return null;
  try {
    const ticket = JSON.parse(raw);
    return ticket && typeof ticket === 'object' ? ticket : null;
  } catch {
    return null;
  }
};

export const getTicketId = (ticket) => {
  if (ticket == null) return '';
  if (typeof ticket === 'string' || typeof ticket === 'number') {
    return String(ticket).trim();
  }
  if (typeof ticket !== 'object') return '';
  const raw = ticket.id ?? ticket.ticketId ?? ticket.ticket_id;
  return raw == null ? '' : String(raw).trim();
};

export const saveTicket = (ticket) => {
  if (!ticket) return;
  const payload = JSON.stringify(ticket);
  try {
    localStorage.setItem(TICKET_STORAGE_KEY, payload);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(TICKET_SESSION_KEY, payload);
  } catch {
    /* ignore */
  }
};

export const getStoredTicket = () => {
  const fromSession = parseTicket(
    (() => {
      try {
        return sessionStorage.getItem(TICKET_SESSION_KEY);
      } catch {
        return null;
      }
    })()
  );
  if (fromSession) return fromSession;

  try {
    return parseTicket(localStorage.getItem(TICKET_STORAGE_KEY));
  } catch {
    return null;
  }
};

export const getStoredTicketId = () => getTicketId(getStoredTicket());

/**
 * Clears the shared localStorage ticket (used when /scan starts fresh).
 * Pass `{ all: true }` to also drop the per-tab sessionStorage backup.
 */
export const clearTicket = ({ all = false } = {}) => {
  try {
    localStorage.removeItem(TICKET_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  if (!all) return;
  try {
    sessionStorage.removeItem(TICKET_SESSION_KEY);
  } catch {
    /* ignore */
  }
};

/** "Ada Lovelace King" → { firstName: "Ada", lastName: "Lovelace King" } */
export const splitName = (fullName) => {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
  };
};

/** Tickets that already carry a name skip the username prompt. */
export const ticketHasName = (ticket) => {
  const records = [];
  if (ticket?.ticket && typeof ticket.ticket === 'object') records.push(ticket.ticket);
  if (ticket && typeof ticket === 'object') records.push(ticket);
  return records.some((record) => {
    const first = String(record.first_name || record.firstName || '').trim();
    const last = String(record.last_name || record.lastName || '').trim();
    return Boolean(first || last);
  });
};

/** Resolve the ticket-linked user id from scan / verify payloads. */
export const ticketUserId = (ticketPayload) => {
  if (!ticketPayload || typeof ticketPayload !== 'object') return '';
  const ticket =
    ticketPayload.ticket && typeof ticketPayload.ticket === 'object'
      ? ticketPayload.ticket
      : ticketPayload;
  const ticketData =
    ticketPayload.ticket_data ||
    ticketPayload.ticketData ||
    ticket.ticket_data ||
    ticket.ticketData ||
    null;
  const nestedTicket =
    ticket.ticket && typeof ticket.ticket === 'object' ? ticket.ticket : null;
  const user =
    ticketData?.user ||
    ticketPayload.user ||
    ticket.user ||
    nestedTicket?.user ||
    null;
  const id =
    (user && typeof user === 'object'
      ? user.id || user.user_id || user.userId
      : user) ||
    ticketData?.user_id ||
    ticketData?.userId ||
    ticket.user_id ||
    ticket.userId ||
    ticketPayload.user_id ||
    ticketPayload.userId ||
    nestedTicket?.user_id ||
    nestedTicket?.userId ||
    '';
  return id == null ? '' : String(id).trim();
};

function pickNamePart(...values) {
  for (const value of values) {
    const text = String(value || '').trim();
    if (text) return text;
  }
  return '';
}

/** First / last name from ticket payload (Creative Studio shop checkout). */
export function ticketNameParts(ticketPayload) {
  if (!ticketPayload || typeof ticketPayload !== 'object') {
    return { firstName: '', lastName: '', fullName: '' };
  }

  const ticket =
    ticketPayload.ticket && typeof ticketPayload.ticket === 'object'
      ? ticketPayload.ticket
      : ticketPayload;
  const ticketData =
    ticketPayload.ticket_data ||
    ticketPayload.ticketData ||
    ticket.ticket_data ||
    ticket.ticketData ||
    {};
  const dataTicket =
    ticketData.ticket && typeof ticketData.ticket === 'object'
      ? ticketData.ticket
      : null;
  const user =
    ticketData.user ||
    ticketPayload.user ||
    ticket.user ||
    dataTicket?.user ||
    {};

  const firstName = pickNamePart(
    dataTicket?.first_name,
    dataTicket?.firstName,
    ticketPayload.firstName,
    ticketPayload.first_name,
    ticket.firstName,
    ticket.first_name,
    ticketData.firstName,
    ticketData.first_name,
    user.firstName,
    user.first_name
  );
  const lastName = pickNamePart(
    dataTicket?.last_name,
    dataTicket?.lastName,
    ticketPayload.lastName,
    ticketPayload.last_name,
    ticket.lastName,
    ticket.last_name,
    ticketData.lastName,
    ticketData.last_name,
    user.lastName,
    user.last_name
  );
  const fullName =
    [firstName, lastName].filter(Boolean).join(' ') ||
    pickNamePart(
      ticketPayload.guestName,
      ticketData.fullName,
      ticketData.full_name,
      ticketData.name,
      user.name,
      ticket.name
    );

  return { firstName, lastName, fullName };
}
